import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { Howl, Howler } from 'howler';

const AudioContext = createContext(null);

export const useAudio = () => useContext(AudioContext);

export const AudioProvider = ({ children }) => {
  const [isMuted, setIsMuted] = useState(false);
  const bgmRef = useRef(null);
  const unlockedRef = useRef(false);
  const audioCtxRef = useRef(null);

  // Initialize sounds and audio engine
  useEffect(() => {
    // 1. Initialize native Web Audio context for procedural sound synthesis
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass && !audioCtxRef.current) {
      audioCtxRef.current = new AudioContextClass();
    }

    // 2. Background Music (Looping via Howler)
    bgmRef.current = new Howl({
      src: ['/sounds/bgm.mp3'],
      loop: true,
      volume: 0.25,
      html5: true,       // use <audio> tag — avoids decodeAudioData compatibility issues
      preload: true,
      onload: () => console.log('[AudioContext] BGM loaded.'),
      onloaderror: (id, err) => console.error('[AudioContext] BGM load error:', err),
    });

    Howler.mute(isMuted);

    const currentBgm = bgmRef.current;
    return () => {
      if (currentBgm) currentBgm.unload();
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close().catch(() => {});
      }
      audioCtxRef.current = null;
      unlockedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync Howler global mute with component state
  useEffect(() => {
    Howler.mute(isMuted);
  }, [isMuted]);

  const toggleMute = useCallback(() => setIsMuted(prev => !prev), []);

  /**
   * startBgm: 必须在用户手势回调中同步调用（点击/触摸事件）
   * 先 resume AudioContext，再调用 play()，满足 iOS Safari 和 Android Chrome 的严格要求
   */
  const startBgm = useCallback(() => {
    if (unlockedRef.current) return; // 已解锁，忽略重复调用
    unlockedRef.current = true;

    // Step 1: resume suspended Web Audio contexts（iOS Safari 必须）
    if (Howler.ctx && Howler.ctx.state === 'suspended') Howler.ctx.resume();
    if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();

    // Step 2: 同步调用 play()（在用户手势栈中）
    if (bgmRef.current && !bgmRef.current.playing() && !isMuted) {
      bgmRef.current.play();
      console.log('[AudioContext] Audio engines unlocked mapping user gesture.');
    }
  }, [isMuted]);

  const pauseBgm = useCallback(() => {
    if (bgmRef.current && bgmRef.current.playing()) {
      bgmRef.current.pause();
    }
  }, []);

  // --------------- Procedural Sound Engine ---------------
  // Synthesizes elegant, lightweight UI sounds on the fly using Web Audio operators
  
  const playTone = (ctx, freq, type, startTime, duration, vol = 0.3) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, startTime);
    
    // Envelope: sharp attack, exponential decay for marimba/bell feel
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(vol, startTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(startTime);
    osc.stop(startTime + duration);
  };

  const playSfx = useCallback((name) => {
    if (isMuted || !audioCtxRef.current) return;
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume();

    const t = ctx.currentTime;
    
    // Dedicated oscillators for sweeping/morphing sounds
    const createSweep = (type, startFreq, endFreq, dur, vol, holdTime = 0.01) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(startFreq, t);
      osc.frequency.exponentialRampToValueAtTime(endFreq, t + dur);
      
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(vol, t + holdTime);
      gain.gain.exponentialRampToValueAtTime(0.01, t + dur);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + dur);
    };

    switch (name) {
      case 'draw':
        // Crisp card swish (smooth quick rise up)
        createSweep('sine', 400, 1000, 0.12, 0.2, 0.05);
        break;

      case 'play':
      case 'hit':
        // Wooden/ivory tile "Thunk/Click", brief low-pitched plunge
        createSweep('triangle', 300, 40, 0.08, 0.6, 0.01);
        break;

      case 'phase_complete':
        // A magical/rewarding chime scale (Tada!) using sine + triangle layered
        playTone(ctx, 523.25, 'sine', t, 0.3, 0.15); // C5
        playTone(ctx, 659.25, 'sine', t + 0.1, 0.3, 0.15); // E5
        playTone(ctx, 783.99, 'sine', t + 0.2, 0.3, 0.15); // G5
        playTone(ctx, 1046.50, 'triangle', t + 0.35, 0.6, 0.2); // C6 (Bright finish)
        break;
        
      case 'win':
        // Triumphant Fanfare (Brass-like square wave)
        playTone(ctx, 523.25, 'square', t, 0.15, 0.1);
        playTone(ctx, 523.25, 'square', t + 0.15, 0.15, 0.1);
        playTone(ctx, 523.25, 'square', t + 0.3, 0.15, 0.1);
        playTone(ctx, 783.99, 'square', t + 0.45, 0.8, 0.2); // Hold G5
        break;

      case 'error':
        // Low dull buzz (Invalid action)
        createSweep('sawtooth', 150, 100, 0.25, 0.2, 0.05);
        break;

      default:
        console.warn(`[AudioContext] Synthesizer effect '${name}' undefined.`);
        break;
    }
  }, [isMuted]);

  return (
    <AudioContext.Provider value={{ isMuted, toggleMute, startBgm, pauseBgm, playSfx }}>
      {children}
    </AudioContext.Provider>
  );
};
